import { settingsApi } from '@/api'
import { GENERAL_SETTINGS_STORAGE_KEY } from '@/config/storageKeys'
import type { TenantSettings, UserSettings } from '@/stores/auth'
import { extractErrorMessage } from '@/utils/errorHandler'
import { create } from 'zustand'
import { devtools, persist, subscribeWithSelector } from 'zustand/middleware'

export interface GeneralSettingsStore {
	userSettings: UserSettings | null
	tenantSettings: TenantSettings | null
	loading: boolean
	error: string | null

	getUserSettings: () => Promise<void>
	updateUserSettings: (newSettings: UserSettings) => Promise<void>
	updateTenantSettings: (newSettings: TenantSettings) => Promise<void>

	setUserSettings: (userSettings: UserSettings | null) => void
	setTenantSettings: (tenantSettings: TenantSettings | null) => void
	setLoading: (loading: boolean) => void
	setError: (error: string | null) => void
	clearError: () => void
}

const initialState: Pick<GeneralSettingsStore, 'userSettings' | 'tenantSettings' | 'loading' | 'error'> = {
	userSettings: null,
	tenantSettings: null,
	loading: false,
	error: null,
}

export const useGeneralSettingsStore = create<GeneralSettingsStore>()(
	devtools(
		persist(
			subscribeWithSelector(set => ({
				...initialState,

				getUserSettings: async () => {
					set({ loading: true, error: null })
					try {
						const userSettings = await settingsApi.getUserSettings()
						set({ userSettings, loading: false })
					} catch (err) {
						set({ error: extractErrorMessage(err, 'Failed to get user settings'), loading: false })
					}
				},

				updateUserSettings: async newSettings => {
					set({ loading: true, error: null })
					try {
						const updatedUserSettings = await settingsApi.updateUserSettings({
							settings: newSettings,
						})
						set({ userSettings: updatedUserSettings, loading: false })
					} catch (err) {
						set({ error: (err as Error).message, loading: false })
					}
				},

				updateTenantSettings: async newSettings => {
					set({ loading: true, error: null })
					try {
						// Sanitize payload for API - remove read-only fields
						const apiPayload: TenantSettings = {
							name: newSettings.name,
							frontend_settings: newSettings.frontend_settings,
							redirect_firmware_downloads_to_cdn: newSettings.redirect_firmware_downloads_to_cdn,
						}

						const updatedTenantSettings = await settingsApi.updateTenantSettings({ settings: apiPayload })

						// If API returns partial data or null, fallback to newSettings to avoid reverting to defaults
						// This fixes the glitch where settings disappear momentarily
						const finalSettings = { ...newSettings, ...(updatedTenantSettings || {}) }

						set({ tenantSettings: finalSettings, loading: false })

						// Also update the authStore's user tenant-settings to keep sidebar in sync (lazy import avoids circular dependency)
						const { useAuthStore } = await import('@/stores/authStore')
						const authState = useAuthStore.getState()
						if (authState.user) {
							useAuthStore.setState({
								user: {
									...authState.user,
									'tenant-settings': finalSettings,
								},
							})
						}
					} catch (err) {
						set({ error: (err as Error).message, loading: false })
						throw err
					}
				},

				setUserSettings: userSettings => set({ userSettings }),
				setTenantSettings: tenantSettings => set({ tenantSettings }),
				setLoading: loading => set({ loading }),
				setError: error => set({ error }),
				clearError: () => set({ error: null }),
			})),
			{
				name: GENERAL_SETTINGS_STORAGE_KEY,
				partialize: state => ({
					userSettings: state.userSettings,
					tenantSettings: state.tenantSettings,
				}),
			},
		),
	),
)

// Selectors (optional for cleaner usage)
export const useUserSettings = () => useGeneralSettingsStore(state => state.userSettings)
